/**
 * Launch-mode sibling discovery (2026-05-23, Phase 1).
 *
 * A "Launch" VirtualSpace renders a single deliverable. When that
 * deliverable belongs to a multi-deliverable capture (e.g. 3 floors
 * of one building captured as separate Matterport models, or N
 * repeat-captures of the same floor over time), customers benefit
 * from being able to navigate between siblings WITHOUT going back
 * to the operator's send-and-resend workflow.
 *
 * **Phase 1 grouping rule** (zero schema change):
 *   Siblings = same `(location_id, program_id, deliverable_type)`
 *
 * **Auto SpaceLink** (2026-10-02) widens "same location" to "same
 *   address": location rows in the same program whose street line and
 *   postal code normalise to the same key (`spacelink-address.ts`) link
 *   too, so floors or later visits filed as separate locations at one
 *   building still share a menu. Each sibling carries its capture date
 *   and the default set is ordered naturally by floor/name across dates.
 *
 * Rationale (locked in 2026-05-23 design session):
 *   - "Same order" was rejected: chain rollouts (29 retail locations
 *     in one order) would surface 29 sibling buttons, making no
 *     sense to a customer viewing one location.
 *   - "Same location" alone is too loose: would mix captures from
 *     completely unrelated engagements over years.
 *   - "Same location + same program" gives the cleanest semantic:
 *     either "floors of the same building captured in one project"
 *     OR "repeat captures of the same space over a program's life"
 *     (e.g. quarterly progress scans of the same floor).
 *
 * **Phase 2** (migration 428, 2026-05-23):
 *   Explicit `launch_bundles` table for operator-curated groupings
 *   that cross location/program boundaries. The loader checks for
 *   an explicit bundle FIRST; auto-fallback only fires when no
 *   bundle covers the current deliverable. Same `LaunchSiblingSet`
 *   shape either way, just with `fromExplicitBundle: true` flipped
 *   when the explicit path wins (consumers don't need to know
 *   which path supplied the siblings).
 *
 *   Naming: the schema entity is `virtualspace.launch_bundles`
 *   (NOT `deliverable_bundles` — that name is already taken by a
 *   pre-existing marketing/pricing bundle concept in the public
 *   order wizard. Keeping the namespaces distinct).
 *
 * **Returned shape**:
 *   - Includes the current deliverable as a sibling (so the menu
 *     can render it with `isCurrent: true`).
 *   - Auto order: numeric floor ascending, then natural model names.
 *     Dates break identical-name ties; existing bundle order is preserved.
 *   - Label cascade: model_label > space.floor_label > space.name
 *     > "Model N" auto-numbered (deterministic ordering).
 *
 * **Labels** (in priority order):
 *   1. `deliverables.model_label` if operator set it
 *   2. `spaces.floor_label` if the deliverable's space has a floor
 *   3. `spaces.name` if the space has a name
 *   4. `"Model {index}"` auto-numbered fallback (never NULL)
 */

import type { SupabaseClient } from "@supabase/supabase-js";

import { normalizeStreetAddress } from "@/lib/virtualspace/spacelink-address";
import { orderSpaceLinksByFloorAndName, pickCaptureDate, pickMissionDate } from "@/lib/virtualspace/spacelink-groups";
import { applySpaceLinkMenuPreferences, type SpaceLinkMenuPreferences } from "./spacelink-menu";
import { applyLaunchPlaceOverrides, mattertagStops, type LaunchPlaceLinks, type LaunchPlaceOverrides, type LaunchReelStop, type LaunchTagStop } from "./launch-stops";

export type LaunchSibling = {
  /** `virtualspace.deliverables.id` — stable identifier for the
   *  sibling. Used as the `#m=` hash value for in-page switching. */
  deliverableId: string;
  /** Matterport SID (or other provider model id) needed by the SDK
   *  to render the actual viewer. Null when the sibling hasn't been
   *  linked to a provider model yet — those are filtered out of the
   *  menu since they can't be displayed. */
  providerModelId: string | null;
  /** Human-readable label shown in the menu / elevator strip. */
  label: string;
  /** The team's own menu name for this model (SpaceLink settings), shown as is. */
  menuLabel?: string;
  /** Sibling's own atomic share token — used to update the URL via
   *  `history.pushState` when the customer switches models, so a
   *  refresh / copy-link captures the new sibling's canonical URL.
   *  Null when no public share exists for that sibling. */
  shareToken: string | null;
  /** True when this sibling matches the deliverable currently
   *  rendered. The menu uses this to show a checkmark / active
   *  state; the parent component uses it to seed `activeModelId`. */
  isCurrent: boolean;
  /** Date the scan is filed under (`YYYY-MM-DD`): its visit (mission
   *  actual start), else the mission date, else Matterport's capture
   *  date, else the deliverable's creation (`pickCaptureDate`). Drives the menu's
   *  month/year sections. Optional so older callers and fixtures that
   *  predate SpaceLink still type-check. */
  capturedAt?: string | null;
  /** Preserve explicit tour order instead of re-sectioning by capture month. */
  customMenuOrder?: boolean;
  /** Default name/floor or persisted bundle order, or a per-tour override. */
  menuOrder?: "natural" | "bundle" | "custom";
  /** Synced Mattertags the menu lists under this model as jump links.
   *  Labels and provider ids only (`launch-stops.ts`). */
  stops?: LaunchTagStop[];
  /** Highlight reel stops read before the model is opened (`launch-live-places.ts`). */
  highlights?: LaunchReelStop[];
};

/** Opt-in operator context, omitted from public sibling payloads. */
export type LaunchSiblingOperatorMetadata = {
  /** Authenticated thumbnail proxy; stored provider image URLs expire. */
  thumbnailUrl: string | null;
  /** Mission actual/scheduled start date only, never provider/creation fallback. */
  missionDate: string | null;
};

export type LaunchSiblingSet<T extends LaunchSibling = LaunchSibling> = {
  /** Every sibling (including the current one) that's renderable.
   *  Sorted in display order. */
  siblings: T[];
  /** True when an explicit `launch_bundles` set supplies the candidates. */
  fromExplicitBundle: boolean;
  /** Auto path only: how many location rows the same-address match
   *  joined (1 when every sibling sits under the current location). */
  linkedLocationCount?: number;
};

/** Split an id list so no PostgREST `.in()` URL grows too long. */
function chunkIds<T>(list: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += 200) out.push(list.slice(i, i + 200));
  return out;
}

/** Visit dates per mission: `[actual_start_at, scheduled_start_at]`. */
async function loadMissionVisitDates(
  supabase: SupabaseClient,
  missionIds: string[],
): Promise<Map<string, { actual: string | null; scheduled: string | null }>> {
  const out = new Map<string, { actual: string | null; scheduled: string | null }>();
  const unique = Array.from(new Set(missionIds));
  const results = await Promise.all(
    chunkIds(unique).map((ids) =>
      supabase
        .schema("virtualspace")
        .from("missions")
        .select("id, actual_start_at, scheduled_start_at")
        .in("id", ids),
    ),
  );
  for (const r of results.flatMap((c) => c.data ?? [])) {
    out.set(r.id as string, {
      actual: (r.actual_start_at as string | null) ?? null,
      scheduled: (r.scheduled_start_at as string | null) ?? null,
    });
  }
  return out;
}

/**
 * Same-program location ids at the current location's street address,
 * always including the current location. Falls back to just the current
 * location when its address is incomplete or the lookup fails.
 */
async function loadSameAddressLocationIds(
  supabase: SupabaseClient,
  args: { locationId: string; programId: string },
): Promise<string[]> {
  const { data: rows, error } = await supabase
    .schema("virtualspace")
    .from("locations")
    .select("id, address_line_1, postal_code")
    .eq("program_id", args.programId)
    .limit(5000);
  if (error || !rows) return [args.locationId];
  const typed = rows as Array<{
    id: string;
    address_line_1: string | null;
    postal_code: string | null;
  }>;
  const current = typed.find((r) => r.id === args.locationId);
  const key = current
    ? normalizeStreetAddress({
        addressLine1: current.address_line_1,
        postalCode: current.postal_code,
      })
    : null;
  if (!key) return [args.locationId];
  const ids = typed
    .filter(
      (r) =>
        normalizeStreetAddress({
          addressLine1: r.address_line_1,
          postalCode: r.postal_code,
        }) === key,
    )
    .map((r) => r.id);
  return ids.includes(args.locationId) ? ids : [args.locationId, ...ids];
}

/**
 * Pure floor signal parser; ordinary numeric names use natural name sorting.
 *
 * Floor labels parse leniently: "B1" / "Basement 1" / "Ground" /
 * "G" / "L1" / "Floor 2" / "2F" / "2" all extract a signed integer
 * ordering ("Basement" / "Cellar" / "BN" → negative, "Ground" / "G"
 * → 0, "Floor N" / "LN" / "NF" / bare N → numeric). Falls back to
 * Number.MAX_SAFE_INTEGER when no numeric signal exists so unknown
 * labels sort to the end.
 */
export function floorSortKey(label: string | null): number {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const lc = label.trim().toLowerCase();
  if (!lc) return Number.MAX_SAFE_INTEGER;
  // Basement / cellar / sub-cellar → negative.
  const basement = lc.match(/^(?:basement|cellar|sub[- ]cellar)\b(?:\s+(\d+)\b)?/);
  if (basement) {
    return basement[1] ? -1 * parseInt(basement[1], 10) : -1;
  }
  const basementCode = lc.match(/^b(\d+)\b/);
  if (basementCode) return -1 * parseInt(basementCode[1]!, 10);
  // Ground / lobby / mezzanine → 0 / 0.5 (mezzanine slightly above).
  if (/^(ground\b|lobby\b|main\b|g$|gf$)/.test(lc)) return 0;
  if (/^mezz/.test(lc)) return 0.5;
  // Roof / rooftop / penthouse → very high but below MAX.
  if (/^(roof\b|rooftop\b|penthouse\b|ph\b)/.test(lc)) return 999;
  const ordinal = lc.match(/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)\s+(?:floor|level|storey|story)\b/);
  if (ordinal) {
    return ["first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth", "eleventh", "twelfth"].indexOf(ordinal[1]!) + 1;
  }
  // Numeric floor (with or without prefix/suffix).
  const m = lc.match(/\b(?:floor|level|storey|story)\s*[:#]?\s*(-?\d+)\b/)
    ?? lc.match(/^l\s*(-?\d+)\b/)
    ?? lc.match(/(?:^|[^\w-])(-?\d+)(?:st|nd|rd|th)?\s*(?:floor|level|f)\b/)
    ?? lc.match(/^(-?\d+)$/);
  if (m) return parseInt(m[1]!, 10);
  return Number.MAX_SAFE_INTEGER;
}

/**
 * Build labels for a set of siblings using the documented cascade.
 * Exported as a pure helper for tests + reuse.
 */
export function buildSiblingLabel(args: {
  modelLabel: string | null;
  spaceFloorLabel: string | null;
  spaceName: string | null;
  locationName: string | null;
  fallbackIndex: number;
}): string {
  const trim = (s: string | null) => (s && s.trim() ? s.trim() : null);
  return (
    trim(args.modelLabel) ??
    trim(args.spaceFloorLabel) ??
    trim(args.spaceName) ??
    trim(args.locationName) ??
    `Model ${args.fallbackIndex + 1}`
  );
}

type LaunchSiblingOptions = {
  menuPreferences?: SpaceLinkMenuPreferences | null;
  /** The tour's menu link source; tag stops ship only for "tags" (the default). */
  placeLinks?: LaunchPlaceLinks;
  /** The tour's hidden and renamed places; hidden tags never ship. */
  placeOverrides?: LaunchPlaceOverrides | null;
  includeOperatorMetadata?: boolean;
};

/**
 * Main loader. Service-client safe (no auth required — siblings
 * resolved server-side then surfaced into the public Launch page).
 * Operator-only callers opt into thumbnail and Mission date context.
 *
 * Two-phase resolution:
 *   1. Check `launch_bundle_members` for explicit operator-curated
 *      groupings (Phase 2). If found, return ALL bundle members.
 *      `member_label` overrides win over the auto-cascade.
 *   2. Else fall through to auto-fallback by
 *      `(location_id, program_id, deliverable_type)` (Phase 1).
 *
 * Returns `siblings: []` when:
 *   - Current deliverable id doesn't exist
 *   - No bundle covers it AND it has no `(location, program)` keys
 *   - No other deliverables match either path
 *
 * In all empty cases the caller renders today's single-model UI.
 */
export function loadLaunchSiblings(
  supabase: SupabaseClient,
  currentDeliverableId: string,
  options: LaunchSiblingOptions & { includeOperatorMetadata: true },
): Promise<LaunchSiblingSet<LaunchSibling & LaunchSiblingOperatorMetadata>>;
export function loadLaunchSiblings(
  supabase: SupabaseClient,
  currentDeliverableId: string,
  options?: LaunchSiblingOptions,
): Promise<LaunchSiblingSet>;
export async function loadLaunchSiblings(
  supabase: SupabaseClient,
  currentDeliverableId: string,
  options?: LaunchSiblingOptions,
): Promise<LaunchSiblingSet> {
  const empty: LaunchSiblingSet = {
    siblings: [],
    fromExplicitBundle: false,
  };
  if (!currentDeliverableId) return empty;

  // 1. Pull the current deliverable's grouping keys + display fields
  //    in one round-trip. We need: location_id, program_id,
  //    organization_id (tenant guard), deliverable_type (filter for
  //    same-type only — siblings of a Matterport are other Matter-
  //    ports, not a BIM model that happens to be in the same loc).
  const { data: current, error: currentErr } = await supabase
    .schema("virtualspace")
    .from("deliverables")
    .select(
      "id, organization_id, location_id, program_id, deliverable_type, space_id, model_label, public_share_token, atomic_virtual_space_id",
    )
    .eq("id", currentDeliverableId)
    .maybeSingle();
  if (currentErr || !current) return empty;

  // 2. Phase 2 — explicit bundle takes precedence. Look up the
  //    current deliverable's bundle membership; if found, return
  //    ALL bundle members (regardless of location / program).
  const withTagStops = (options?.placeLinks ?? "tags") === "tags";
  const tagStopsFor = (stops: LaunchTagStop[] | undefined) =>
    withTagStops ? applyLaunchPlaceOverrides(stops ?? [], options?.placeOverrides ?? null) : [];
  const bundleSet = await tryLoadFromExplicitBundle(
    supabase,
    current.id as string,
    options?.includeOperatorMetadata ?? false,
    tagStopsFor,
  );
  if (bundleSet) return { ...bundleSet, siblings: applySpaceLinkMenuPreferences(bundleSet.siblings, options?.menuPreferences ?? null) };

  // 3. Phase 1 auto-fallback path. Requires both keys; the rule
  //    is "same building / same program / same deliverable type".
  if (!current.location_id || !current.program_id) return empty;

  // 4. Fetch ALL deliverables (including current) matching the
  //    grouping keys. Tenant guard via organization_id even though
  //    location_id + program_id are tenant-scoped — defense-in-depth.
  //    "Same location" includes same-address locations in the program.
  const locationIds = await loadSameAddressLocationIds(supabase, {
    locationId: current.location_id as string,
    programId: current.program_id as string,
  });
  const { data: rows, error: siblingsErr } = await supabase
    .schema("virtualspace")
    .from("deliverables")
    .select(
      "id, model_label, space_id, public_share_token, atomic_virtual_space_id, created_at, status, location_id, mission_id",
    )
    .eq("organization_id", current.organization_id)
    .in("location_id", locationIds)
    .eq("program_id", current.program_id)
    .eq("deliverable_type", current.deliverable_type)
    .neq("status", "cancelled")
    .order("created_at", { ascending: true });
  if (siblingsErr || !rows || rows.length === 0) return empty;

  // Filter out any "internal" / archived statuses that shouldn't
  // surface in customer-facing nav. Renderable statuses are anything
  // the customer can actually open — for matterport that's once a
  // model id is wired, so we'll also drop sibling rows whose model
  // hasn't landed yet at the very end.
  const renderableStatuses = new Set([
    "requested",
    "in_progress",
    "in_review",
    "needs_changes",
    "approved",
    "completed",
    "delivered",
    "open",
    "active",
  ]);
  const eligible = rows.filter((r) =>
    renderableStatuses.has(String(r.status ?? "")),
  );
  if (eligible.length === 0) return empty;

  // 3. Bulk-fetch the provider model ids + space labels in 2 more
  //    parallel round-trips. Avoids N+1.
  const deliverableIds = eligible.map((r) => r.id as string);
  const spaceIds = Array.from(
    new Set(
      eligible
        .map((r) => r.space_id as string | null)
        .filter((s): s is string => Boolean(s)),
    ),
  );

  // Chunk the id lists: a building documented over years can carry
  // hundreds of scans, and one `.in()` that long overflows the URL.
  const [mpChunks, missionDates, { data: spaceRows }] = await Promise.all([
    Promise.all(
      chunkIds(deliverableIds).map((ids) =>
        supabase
          .schema("virtualspace")
          .from("matterport_details")
          .select(
            "deliverable_id, provider_model_id, provider_captured_at, provider_mattertags, payload_created_at:last_provider_payload->>createdAt",
          )
          .in("deliverable_id", ids),
      ),
    ),
    loadMissionVisitDates(
      supabase,
      eligible
        .map((r) => r.mission_id as string | null)
        .filter((m): m is string => Boolean(m)),
    ),
    spaceIds.length > 0
      ? supabase
          .schema("virtualspace")
          .from("spaces")
          .select("id, name, floor_label")
          .in("id", spaceIds)
      : Promise.resolve({ data: [] as Array<{ id: string; name: string | null; floor_label: string | null }> }),
  ]);

  const modelIdByDeliverable = new Map<string, string | null>();
  const mpByDeliverable = new Map<
    string,
    { captured: string | null; payloadCreated: string | null; stops: LaunchTagStop[] }
  >();
  for (const m of mpChunks.flatMap((c) => c.data ?? [])) {
    modelIdByDeliverable.set(
      m.deliverable_id as string,
      (m.provider_model_id as string | null) ?? null,
    );
    mpByDeliverable.set(m.deliverable_id as string, {
      captured: (m.provider_captured_at as string | null) ?? null,
      payloadCreated: (m.payload_created_at as string | null) ?? null,
      stops: mattertagStops(m.provider_mattertags),
    });
  }
  const spaceById = new Map<
    string,
    { name: string | null; floor_label: string | null }
  >();
  for (const s of spaceRows ?? []) {
    spaceById.set(s.id as string, {
      name: (s.name as string | null) ?? null,
      floor_label: (s.floor_label as string | null) ?? null,
    });
  }

  // 4. Compose siblings with labels + filter to those that actually
  //    have a provider model id (un-linked rows can't render).
  const siblings: LaunchSibling[] = eligible
    .map((r, idx) => {
      const space = r.space_id
        ? spaceById.get(r.space_id as string) ?? null
        : null;
      return {
        deliverableId: r.id as string,
        providerModelId: modelIdByDeliverable.get(r.id as string) ?? null,
        label: buildSiblingLabel({
          modelLabel: (r.model_label as string | null) ?? null,
          spaceFloorLabel: space?.floor_label ?? null,
          spaceName: space?.name ?? null,
          locationName: null,
          fallbackIndex: idx,
        }),
        shareToken: (r.public_share_token as string | null) ?? null,
        isCurrent: r.id === current.id,
        menuOrder: "natural" as const,
        stops: tagStopsFor(mpByDeliverable.get(r.id as string)?.stops),
        ...(options?.includeOperatorMetadata ? {
          thumbnailUrl: `/api/matterport-thumbnail/${encodeURIComponent(r.id as string)}`,
          missionDate: pickMissionDate(r.mission_id ? missionDates.get(r.mission_id as string) : null),
        } : {}),
        capturedAt: pickCaptureDate({
          providerCapturedAt: mpByDeliverable.get(r.id as string)?.captured,
          providerPayloadCreatedAt: mpByDeliverable.get(r.id as string)
            ?.payloadCreated,
          missionActualStartAt: r.mission_id
            ? missionDates.get(r.mission_id as string)?.actual
            : null,
          missionScheduledStartAt: r.mission_id
            ? missionDates.get(r.mission_id as string)?.scheduled
            : null,
          deliverableCreatedAt: r.created_at as string | null,
        }),
      };
    })
    // Drop rows that have no provider model id — they're not
    // renderable in the SDK iframe. Operators will see them on the
    // operator-side picker; customers don't get a broken click.
    .filter((s) => s.providerModelId !== null);

  if (siblings.length === 0) return empty;

  // 5. A flat natural floor/name default across visits. Saved tour overrides win.
  return {
    siblings: applySpaceLinkMenuPreferences(orderSpaceLinksByFloorAndName(siblings), options?.menuPreferences ?? null),
    fromExplicitBundle: false,
    linkedLocationCount: new Set(
      eligible.map((r) => (r.location_id as string | null) ?? current.location_id),
    ).size,
  };
}

/**
 * Phase 2 explicit-bundle resolver. Returns null when the current
 * deliverable isn't in any bundle (caller falls through to the
 * auto-fallback path); returns a fully-populated `LaunchSiblingSet`
 * when it IS.
 *
 * Ordering: respects the operator-set `sort_order` (with `added_at`
 * as tiebreaker, matching the index `idx_launch_bundle_members_bundle_order`).
 * Skips the auto-floor-sort that the auto-fallback path applies —
 * operators explicitly curate the order, so we honor their pick.
 *
 * Label cascade (per member):
 *   1. `launch_bundle_members.member_label` (operator-curated override)
 *   2. `deliverables.model_label`
 *   3. `spaces.floor_label`
 *   4. `spaces.name`
 *   5. `locations.name`
 *   6. `Model N` auto-numbered fallback
 *
 * Filters out members with no provider_model_id (un-renderable —
 * same rule as the auto-fallback path) AND members whose status is
 * not in the renderable set.
 */
async function tryLoadFromExplicitBundle(
  supabase: SupabaseClient,
  currentDeliverableId: string,
  includeOperatorMetadata: boolean,
  tagStopsFor: (stops: LaunchTagStop[] | undefined) => LaunchTagStop[],
): Promise<LaunchSiblingSet | null> {
  // 1. Probe — is the current deliverable in any bundle?
  const { data: membership, error: membershipErr } = await supabase
    .schema("virtualspace")
    .from("launch_bundle_members")
    .select("bundle_id")
    .eq("deliverable_id", currentDeliverableId)
    .maybeSingle();
  if (membershipErr || !membership) return null;

  // 2. Fetch all bundle members, sorted by operator's pick.
  const { data: memberRows, error: memberErr } = await supabase
    .schema("virtualspace")
    .from("launch_bundle_members")
    .select("deliverable_id, sort_order, member_label, added_at")
    .eq("bundle_id", membership.bundle_id as string)
    .order("sort_order", { ascending: true })
    .order("added_at", { ascending: true });
  if (memberErr || !memberRows || memberRows.length === 0) return null;

  const memberDeliverableIds = memberRows.map(
    (m) => m.deliverable_id as string,
  );

  // 3. Bulk-fetch deliverable details + matterport_details +
  //    spaces + locations in 4 parallel round-trips.
  const renderableStatuses = new Set([
    "requested",
    "in_progress",
    "in_review",
    "needs_changes",
    "approved",
    "completed",
    "delivered",
    "open",
    "active",
  ]);

  const { data: deliverableRows } = await supabase
    .schema("virtualspace")
    .from("deliverables")
    .select(
      "id, model_label, space_id, location_id, public_share_token, atomic_virtual_space_id, status, created_at, mission_id",
    )
    .in("id", memberDeliverableIds);
  if (!deliverableRows || deliverableRows.length === 0) return null;

  const renderableDeliverables = deliverableRows.filter((d) =>
    renderableStatuses.has(String(d.status ?? "")),
  );
  if (renderableDeliverables.length === 0) return null;

  const renderableIds = renderableDeliverables.map((d) => d.id as string);
  const spaceIds = Array.from(
    new Set(
      renderableDeliverables
        .map((d) => d.space_id as string | null)
        .filter((s): s is string => Boolean(s)),
    ),
  );
  const locationIds = Array.from(
    new Set(
      renderableDeliverables
        .map((d) => d.location_id as string | null)
        .filter((l): l is string => Boolean(l)),
    ),
  );

  const [{ data: mpRows }, { data: spaceRows }, { data: locationRows }] =
    await Promise.all([
      supabase
        .schema("virtualspace")
        .from("matterport_details")
        .select(
          "deliverable_id, provider_model_id, provider_captured_at, provider_mattertags, payload_created_at:last_provider_payload->>createdAt",
        )
        .in("deliverable_id", renderableIds),
      spaceIds.length > 0
        ? supabase
            .schema("virtualspace")
            .from("spaces")
            .select("id, name, floor_label")
            .in("id", spaceIds)
        : Promise.resolve({
            data: [] as Array<{
              id: string;
              name: string | null;
              floor_label: string | null;
            }>,
          }),
      locationIds.length > 0
        ? supabase
            .schema("virtualspace")
            .from("locations")
            .select("id, name")
            .in("id", locationIds)
        : Promise.resolve({
            data: [] as Array<{ id: string; name: string | null }>,
          }),
    ]);

  const modelIdByDeliverable = new Map<string, string | null>();
  const mpByDeliverable = new Map<
    string,
    { captured: string | null; payloadCreated: string | null; stops: LaunchTagStop[] }
  >();
  for (const m of mpRows ?? []) {
    modelIdByDeliverable.set(
      m.deliverable_id as string,
      (m.provider_model_id as string | null) ?? null,
    );
    mpByDeliverable.set(m.deliverable_id as string, {
      captured: (m.provider_captured_at as string | null) ?? null,
      payloadCreated: (m.payload_created_at as string | null) ?? null,
      stops: mattertagStops(m.provider_mattertags),
    });
  }
  const missionDates = await loadMissionVisitDates(
    supabase,
    renderableDeliverables
      .map((d) => d.mission_id as string | null)
      .filter((m): m is string => Boolean(m)),
  );
  const spaceById = new Map<
    string,
    { name: string | null; floor_label: string | null }
  >();
  for (const s of spaceRows ?? []) {
    spaceById.set(s.id as string, {
      name: (s.name as string | null) ?? null,
      floor_label: (s.floor_label as string | null) ?? null,
    });
  }
  const locationById = new Map<string, string | null>();
  for (const l of locationRows ?? []) {
    locationById.set(l.id as string, (l.name as string | null) ?? null);
  }

  // Index renderable deliverables by id for the member-row walk
  // (preserves sort_order semantics from the membership query).
  const deliverableById = new Map<
    string,
    {
      modelLabel: string | null;
      spaceId: string | null;
      locationId: string | null;
      shareToken: string | null;
      createdAt: string | null;
      missionId: string | null;
    }
  >();
  for (const d of renderableDeliverables) {
    deliverableById.set(d.id as string, {
      modelLabel: (d.model_label as string | null) ?? null,
      spaceId: (d.space_id as string | null) ?? null,
      locationId: (d.location_id as string | null) ?? null,
      shareToken: (d.public_share_token as string | null) ?? null,
      createdAt: (d.created_at as string | null) ?? null,
      missionId: (d.mission_id as string | null) ?? null,
    });
  }

  // 4. Build siblings preserving the operator's order. Drop members
  //    whose deliverable rows didn't survive the renderable-status
  //    filter (defense-in-depth — the FK CASCADE should keep these
  //    in lockstep, but a slow background delete could leave a
  //    membership row dangling for a few ms).
  const siblings: LaunchSibling[] = memberRows
    .map((m, idx): LaunchSibling | null => {
      const deliverableId = m.deliverable_id as string;
      const detail = deliverableById.get(deliverableId);
      if (!detail) return null;
      const space = detail.spaceId
        ? spaceById.get(detail.spaceId) ?? null
        : null;
      const locationName = detail.locationId
        ? locationById.get(detail.locationId) ?? null
        : null;
      return {
        deliverableId,
        providerModelId: modelIdByDeliverable.get(deliverableId) ?? null,
        label: buildSiblingLabel({
          // Member-label override wins over EVERYTHING in the
          // cascade — operators picking a custom label per-bundle
          // (e.g. "Tour stop 1") shouldn't be silently overridden
          // by an automatic space-floor or location-name read.
          modelLabel:
            (m.member_label as string | null) ?? detail.modelLabel,
          spaceFloorLabel: space?.floor_label ?? null,
          spaceName: space?.name ?? null,
          locationName,
          fallbackIndex: idx,
        }),
        shareToken: detail.shareToken,
        isCurrent: deliverableId === currentDeliverableId,
        menuOrder: "bundle",
        stops: tagStopsFor(mpByDeliverable.get(deliverableId)?.stops),
        ...(includeOperatorMetadata ? {
          thumbnailUrl: `/api/matterport-thumbnail/${encodeURIComponent(deliverableId)}`,
          missionDate: pickMissionDate(detail.missionId ? missionDates.get(detail.missionId) : null),
        } : {}),
        capturedAt: pickCaptureDate({
          providerCapturedAt: mpByDeliverable.get(deliverableId)?.captured,
          providerPayloadCreatedAt: mpByDeliverable.get(deliverableId)
            ?.payloadCreated,
          missionActualStartAt: detail.missionId
            ? missionDates.get(detail.missionId)?.actual
            : null,
          missionScheduledStartAt: detail.missionId
            ? missionDates.get(detail.missionId)?.scheduled
            : null,
          deliverableCreatedAt: detail.createdAt,
        }),
      };
    })
    .filter((s): s is LaunchSibling => s !== null)
    .filter((s) => s.providerModelId !== null);

  if (siblings.length === 0) return null;

  return { siblings, fromExplicitBundle: true };
}
